import Box from "@/components/Box";
import { ExternalTextLink } from "@/components/Links";
import OrderStatusBadges from "@/components/OrderStatusBadges";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { getAPIBase } from "@/core/config";
import {
  BASKET_PAYEE_PATIENT,
  ORDER_PRODUCT_REORDER,
  ORDER_RESEND_STATUS,
  ORDER_RETEST_STATUS,
  PAYMENT_OBJECT_STATUS_CREATED,
  PAYMENT_OBJECT_STATUS_PAID
} from "@/core/constants";
import Badge from "@/tpo/Badge";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import { PAYMENT_STATUS_PATIENT_TO_PAY } from "@/tpo/shop/constants";
import ButtonV2 from "@/v2/Buttons";

import getProgressStatusBadgeProps from "../../utils/getProgressStatusBadgeProps";
import classes from "./OrganisationOrderSummary.module.css";
import ResendOrderEmailButton from "./ResendOrderEmailButton";

function StripePaymentLink({ url, text, newTab = false }) {
  return (
    <ExternalTextLink
      onClick={e => {
        e.clickWithinOrganisationOrderSummaryComponent = true;
      }}
      href={url}
    >
      {text}
    </ExternalTextLink>
  );
}

function parseStripeDetails(
  payee,
  paymentCheckoutSession,
  paymentInvoice,
  checkoutRedirectUrl,
  orderId
) {
  if (paymentInvoice?.url) {
    // Paid or not, if there's an invoice URL, show it
    return {
      label: "Payment:",
      value: <StripePaymentLink url={paymentInvoice.url} text="View Invoice" newTab={true} />,
      key: "payment"
    };
  }

  const isPatient = payee === "patient";
  const isPaid = paymentCheckoutSession?.paymentStatus === PAYMENT_OBJECT_STATUS_PAID;

  // Handle paid checkout sessions
  if (isPaid) {
    if (paymentCheckoutSession.invoiceUrl) {
      const text = isPatient ? "View Details" : "View Invoice";
      return [
        {
          label: "Payment receipt:",
          value: (
            <>
              <StripePaymentLink
                url={paymentCheckoutSession.invoiceUrl}
                text="View"
                newTab={true}
              />
            </>
          ),
          key: "paymentReceipt"
        },
        {
          label: "Payment:",
          value: (
            <>
              <StripePaymentLink
                url={paymentCheckoutSession.invoiceUrl}
                text={text}
                newTab={true}
              />
            </>
          ),
          key: "payment"
        }
      ];
    }
    return null;
  }

  // Handle unpaid checkout sessions. Patient-pay uses the tokenised redirect URL;
  // organisation-pay uses the backend checkout endpoint so Stripe sessions can be
  // reused or recreated.
  if (paymentCheckoutSession) {
    const url = isPatient
      ? checkoutRedirectUrl
      : `${getAPIBase()}/payments/checkout/${orderId}/`;
    if (!url) {
      return null;
    }
    const text = isPatient ? "Payment Link" : "Pay now";
    return {
      label: "Payment:",
      value: <StripePaymentLink url={url} text={text} />,
      key: "payment"
    };
  }

  return null;
}

export default function OrganisationOrderSummary({ order }) {
  const { organisation } = useOrganisationContext();

  let stripeDetails = parseStripeDetails(
    order.sourceBasket.payee,
    order.sourceBasket.paymentCheckoutSession,
    order.sourceBasket.paymentInvoice,
    order.patientPayCheckoutRedirectUrl,
    order.id
  );

  stripeDetails = Array.isArray(stripeDetails) ? stripeDetails : [stripeDetails];
  stripeDetails = stripeDetails.filter(Boolean);

  let additionalStatus = null;

  if (order.suppliedFromPracticeStock) {
    additionalStatus = "From Stock";
  } else if (order.isStockOrder) {
    additionalStatus = "Stock Order";
  } else if (
    order.status === ORDER_RESEND_STATUS ||
    order.status === ORDER_RETEST_STATUS ||
    order.status === ORDER_PRODUCT_REORDER
  ) {
    additionalStatus = order.status;
  }

  return (
    <Box
      display="flex"
      flexDirection={["column", "column", "row"]}
      justifyContent={["flex-start", "flex-start", "space-between"]}
      py={4}
      gap={20}
    >
      <Stack gap={10}>
        {additionalStatus && (
          <Box alignItems="flex-start" fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
            <Badge bg="black" color="white" ml="auto" size="xs">
              {additionalStatus}
            </Badge>
          </Box>
        )}
        <Group gap={10}>
          <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
            Order No:
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} data-testid="orderDetails:orderNo">
            {order.id}
          </Box>
        </Group>
        {!!stripeDetails?.length &&
          stripeDetails.map(sd => (
            <Group gap={10} key={sd.key}>
              <Box
                fontFamily="gilroyBold"
                fontSize={[14, 14, 16]}
                data-testid="stripeDetails:label"
              >
                {sd.label}
              </Box>
              <Box
                fontFamily="gilroyMedium"
                fontSize={[14, 14, 16]}
                data-testid="stripeDetails:value"
              >
                {sd.value}
              </Box>
            </Group>
          ))}
        <Group gap={10}>
          <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
            Order Date:
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} data-testid="orderDate">
            {new Date(order.checkoutDate).toLocaleDateString()}
          </Box>
        </Group>
        {order.sourceBasket &&
        order.sourceBasket.paymentInvoice &&
        order.sourceBasket.paymentInvoice.dateDue &&
        order.sourceBasket.paymentInvoice.paymentStatus === PAYMENT_OBJECT_STATUS_CREATED ? (
          <Group gap={10} alignItems="center" flexWrap="wrap">
            <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
              Payment due:
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} data-testid="paymentDue">
              {new Date(order.sourceBasket.paymentInvoice.dateDue).toLocaleDateString()}
            </Box>
            {order.sourceBasket.paymentInvoice.url ? (
              <ButtonV2
                as="a"
                color="green"
                href={order.sourceBasket.paymentInvoice.url}
                sx={{
                  lineHeight: 1,
                  cursor: "pointer",
                  px: 20,
                  py: 2,
                  fontSize: 12
                }}
                data-testid="payNowButton"
                onClick={e => {
                  e.clickWithinOrganisationOrderSummaryComponent = true;
                }}
                size="xs"
              >
                pay now
              </ButtonV2>
            ) : null}
          </Group>
        ) : null}
        {order?.sourceBasket?.payee === BASKET_PAYEE_PATIENT &&
          order.derivedPaymentStatus === PAYMENT_STATUS_PATIENT_TO_PAY && (
            <Group alignItems="center" gap={10}>
              <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                Patient order email:
              </Box>
              <ResendOrderEmailButton order={order} />
            </Group>
          )}
        {!!order.estimatedResultsAvailableDate && (
          <Box>
            <Group gap={10}>
              <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                Results expected:
              </Box>
              <Box
                fontFamily="gilroyMedium"
                fontSize={[14, 14, 16]}
                data-testid="estimatedResultsAvailableDate"
              >
                {new Date(order.estimatedResultsAvailableDate).toLocaleDateString()}
              </Box>
            </Group>
            { !!order.estimatedResultsAvailableDateHelpText && <Box fontSize={14} fontFamily="gilroyMedium" mt={2} ml={24}>
              {order.estimatedResultsAvailableDateHelpText}
            </Box>}
          </Box>
        )}
        {!!order.userFullName && (
          <Group gap={10}>
            <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
              Name:
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} data-testid="orderName">
              {order.userFullName}
            </Box>
          </Group>
        )}
        {!!order.testSummary && (
          <Group gap={10}>
            <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
              Tests:
            </Box>
            <Box
              fontFamily="gilroyMedium"
              fontSize={[14, 14, 16]}
              data-testid="orderTestSummary"
              className={classes.truncateText}
            >
              {JSON.parse(order.testSummary)
                .map(
                  test => `${test.count}x ${test.product_name} ${test.product_option_name || ""}`
                )
                .join(", ")}
            </Box>
          </Group>
        )}
      </Stack>
      <OrderStatusBadges
        order={order}
        showPaymentStatus={order.status != ORDER_RETEST_STATUS}
        progressStatusBadgeProps={getProgressStatusBadgeProps({
          organisationId: organisation?.hashId,
          patientId: order?.userId,
          derivedProgressStatus: order?.derivedProgressStatus
        })}
      />
    </Box>
  );
}
